/**
 * Shared constants and types for the messaging feature.
 */

// Custom event name dispatched when conversation list should refresh.
export const MARKETPLACE_CONVERSATION_REFRESH_EVENT =
  'marketplace:conversations-updated';

/**
 * Fire a conversation refresh event so sidebar + unread badge re-fetch.
 */
export function notifyConversationsUpdated(): void {
  window.dispatchEvent(new Event(MARKETPLACE_CONVERSATION_REFRESH_EVENT));
}

// ---------------------------------------------------------------------------
// Shared types – keep in sync with API response shapes.
// ---------------------------------------------------------------------------

export interface ConversationListItem {
  id: string;
  otherBusinessId: string | null;
  otherName: string;
  otherAvatarUrl: string | null;
  lastMessage: {
    body: string;
    messageType: string;
    createdAt: string;
  } | null;
  unreadCount: number;
  updatedAt: string;
}

export interface BusinessSummary {
  id: string;
  name: string;
  slug?: string;
  avatarUrl?: string | null;
  avgRating?: number | null;
  ratingCount?: number;
  locationNeighborhood?: string | null;
  locationCity?: string | null;
  addressStreet?: string | null;
  owner?: { name: string | null };
}

export interface ConversationParty {
  id: string;
  businessId?: string | null;
  displayName?: string | null;
  business?: BusinessSummary | null;
}

export interface MessageSender {
  id: string;
  business?: { id: string; name: string; owner?: { name: string | null } } | null;
}

export interface OfferPartySummary {
  id: string;
  business?: { id: string; name: string } | null;
}

export interface OfferRevision {
  revisionNo: number;
  price?: number | null;
  quantity?: number | null;
  proposedByParty?: OfferPartySummary | null;
}

export interface OfferSummary {
  id: string;
  status?: string;
  effectiveStatus?: string;
  price?: number | null;
  quantity?: number | null;
  expiresAt?: string | null;
  deliveryAddress?: string | null;
  deliveryNotes?: string | null;
  product?: {
    id: string;
    title?: string;
    unit?: string;
  } | null;
  buyPost?: {
    id: string;
    title?: string;
  } | null;
  // MarketplaceSlot has no `label` column — the field could never be populated.
  slotOptions?: Array<{ slot?: { id: string } | null }>;
  buyerParty?: OfferPartySummary | null;
  sellerParty?: OfferPartySummary | null;
  revisions?: OfferRevision[];
  selectedSlotId?: string | null;
}

export interface ConversationMessage {
  id: string;
  body: string;
  messageType?: string;
  createdAt: string;
  senderParty?: MessageSender | null;
  offer?: OfferSummary | null;
}

export interface ConversationDetail {
  id: string;
  partyAId: string;
  partyBId: string;
  partyA?: ConversationParty | null;
  partyB?: ConversationParty | null;
  messages: ConversationMessage[];
}
